import { deepEvent, stats } from "@/mocks/gossip";
import { SectionHeading } from "./SectionHeading";

export default function Timeline() {
  return (
    <section id="timeline" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <SectionHeading
          kicker="深扒 · 完整时间线"
          title="年度大瓜全记录：从源头到尾声"
          desc={deepEvent.summary}
        />
        <div className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-800 whitespace-nowrap">
          <i className="ri-time-line"></i>
          已归档 {deepEvent.nodes.length} 个关键节点
        </div>
      </div>

      {/* cover + summary */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src={deepEvent.cover}
              alt="顶流演员片约风波完整时间线封面"
              className="h-56 w-full object-cover md:h-72"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-5 md:p-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-flag-2-line"></i>
                主档案 · {deepEvent.subtitle}
              </div>
            </div>
          </div>

          {/* stats band */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-background-200 bg-background-100 px-4 py-3 text-center">
                <div className="font-heading text-xl font-black text-primary-700 md:text-2xl">{s.num}</div>
                <div className="mt-1 text-[11px] text-foreground-500 md:text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* quick jump sticky card */}
        <aside className="h-fit rounded-2xl border border-background-200 bg-background-100 p-5 lg:sticky lg:top-24">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-background-50">
              <i className="ri-list-check-2"></i>
            </span>
            <h3 className="font-heading text-base font-bold text-foreground-950">本篇档案目录</h3>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-600">
            事件跨度 87 天，共 {deepEvent.nodes.length} 个节点。建议先读前 3 节点了解起因，再按需跳转。
          </p>
          <ol className="mt-4 space-y-1.5">
            {deepEvent.nodes.map((n, i) => (
              <li key={i}>
                <a
                  href={`#node-${i}`}
                  className="group flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700"
                >
                  <span className="w-7 shrink-0 text-xs font-semibold text-secondary-600">0{i + 1}</span>
                  <span className="truncate">{n.title}</span>
                </a>
              </li>
            ))}
          </ol>
          <div className="mt-4 rounded-xl border border-accent-200 bg-accent-50 px-4 py-3">
            <p className="text-xs leading-relaxed text-accent-900">
              <i className="ri-information-line mr-1"></i>
              标注为"待核实"的节点不构成既定事实，仅供信息参考。
            </p>
          </div>
        </aside>
      </div>

      {/* timeline nodes */}
      <div className="relative mt-8 ml-1 border-l-2 border-background-200 pl-6 md:ml-4 md:pl-10">
        {deepEvent.nodes.map((n, i) => (
          <div key={i} id={`node-${i}`} className="relative pb-8 last:pb-0">
            <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center md:-left-[47px]">
              <span className="h-4 w-4 rounded-full border-4 border-background-50 bg-primary-500 shadow-sm"></span>
            </span>
            <div className="flex flex-col gap-1.5 md:flex-row md:items-center">
              <div className="inline-flex items-center gap-2 md:w-40 md:shrink-0">
                <span className="font-heading text-base font-bold text-foreground-950">{n.date}</span>
                <span className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-[11px] font-medium text-secondary-900">{n.time}</span>
              </div>
              <div className="flex-1 rounded-2xl border border-background-200 bg-background-100/80 p-4 md:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-base font-bold text-foreground-950">
                    <span className="mr-2 text-secondary-500">#{String(i + 1).padStart(2, "0")}</span>
                    {n.title}
                  </h4>
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap " +
                      (n.source.includes("待核实")
                        ? "bg-secondary-100 text-secondary-900"
                        : "bg-accent-100 text-accent-800")
                    }
                  >
                    <i className={n.source.includes("待核实") ? "ri-alarm-warning-line" : "ri-verified-badge-line"}></i>
                    {n.source}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{n.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}